import { memo } from 'react';
import { Gift, ListChecks, MapPin, Sparkles } from 'lucide-react';
import { ItemIcon } from '../../ItemIcon';
import { displayQuestTitle, type QuestNode } from '../../../utils/questSnbt/model';
import { asArray, unwrapNumber, unwrapString } from '../../../utils/questSnbt/unwrap';

export interface QuestDetailPanelProps {
  quest: QuestNode | null;
  clusterId: string;
  chapterTitle: string;
}

export const QuestDetailPanel = memo(function QuestDetailPanel({
  quest,
  clusterId,
  chapterTitle,
}: QuestDetailPanelProps) {
  if (!quest) {
    return (
      <div className="h-full flex flex-col items-center justify-center text-center px-4 py-8 text-glass-400">
        <Sparkles className="w-8 h-8 mb-3 text-accent-400/70" />
        <p className="text-sm text-glass-300">Select a quest</p>
        <p className="text-xs mt-1 text-glass-500">
          Click a node on {chapterTitle || 'the chapter'} to inspect tasks, rewards, and icons.
        </p>
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto px-3 py-3 space-y-3">
      <div className="flex items-start gap-3">
        <div className="w-12 h-12 flex-shrink-0 rounded-lg bg-glass-100/10 border border-glass-200/15 flex items-center justify-center overflow-hidden">
          {quest.iconItemId ? (
            <ItemIcon
              clusterId={clusterId}
              itemId={quest.iconItemId}
              displayMode="icon"
              className="w-10 h-10"
            />
          ) : (
            <Sparkles className="w-5 h-5 text-amber-300" />
          )}
        </div>
        <div className="min-w-0">
          <h3 className="text-sm font-semibold text-white leading-snug">{displayQuestTitle(quest.title)}</h3>
          {quest.subtitle && <p className="text-xs text-glass-400 mt-0.5">{quest.subtitle}</p>}
          <p className="text-[10px] font-mono text-glass-500 mt-1 break-all">{quest.id}</p>
        </div>
      </div>

      <div className="flex flex-wrap gap-1.5 text-[10px]">
        <Chip>{quest.shape || 'circle'}</Chip>
        {quest.optional && <Chip tone="sky">optional</Chip>}
        <Chip>
          {quest.x.toFixed(2)}, {quest.y.toFixed(2)}
        </Chip>
        {quest.dependencies.length > 0 && <Chip>{quest.dependencies.length} deps</Chip>}
      </div>

      {quest.description.length > 0 && (
        <div className="space-y-1">
          {quest.description.map((line, i) => (
            <p key={i} className="text-xs text-glass-300 leading-relaxed">
              {line}
            </p>
          ))}
        </div>
      )}

      <Section icon={ListChecks} label={`Tasks (${quest.tasks.length})`}>
        {quest.tasks.length === 0 ? (
          <p className="text-xs text-glass-500">No tasks.</p>
        ) : (
          <ul className="space-y-1.5">
            {quest.tasks.map((task, i) => (
              <li
                key={task.id ?? i}
                className="flex items-center gap-2 rounded-md bg-glass-100/5 border border-glass-200/10 px-2 py-1.5"
              >
                {task.itemId ? (
                  <ItemIcon
                    clusterId={clusterId}
                    itemId={task.itemId}
                    displayMode="inline"
                    className="w-5 h-5 flex-shrink-0"
                  />
                ) : (
                  <ListChecks className="w-4 h-4 text-glass-500 flex-shrink-0" />
                )}
                <div className="min-w-0">
                  <p className="text-xs text-glass-200 truncate">
                    {task.title || humanizeType(task.type)}
                    {task.count != null && task.type !== 'checkmark' ? ` ×${task.count}` : ''}
                  </p>
                  <p className="text-[10px] text-glass-500 font-mono truncate">{task.type}</p>
                </div>
              </li>
            ))}
          </ul>
        )}
      </Section>

      <Section icon={Gift} label={`Rewards (${quest.rewards.length})`}>
        {quest.rewards.length === 0 ? (
          <p className="text-xs text-glass-500">No rewards.</p>
        ) : (
          <ul className="space-y-1.5">
            {quest.rewards.map((reward, i) => (
              <li
                key={reward.id ?? i}
                className="flex items-center gap-2 rounded-md bg-glass-100/5 border border-glass-200/10 px-2 py-1.5"
              >
                {reward.itemId ? (
                  <ItemIcon
                    clusterId={clusterId}
                    itemId={reward.itemId}
                    displayMode="inline"
                    className="w-5 h-5 flex-shrink-0"
                  />
                ) : (
                  <Gift className="w-4 h-4 text-amber-300/80 flex-shrink-0" />
                )}
                <div className="min-w-0">
                  <p className="text-xs text-glass-200 truncate">
                    {reward.title || humanizeType(reward.type)}
                    {reward.count != null ? ` ×${reward.count}` : ''}
                  </p>
                  <p className="text-[10px] text-glass-500 font-mono truncate">{reward.type}</p>
                </div>
              </li>
            ))}
          </ul>
        )}
      </Section>

      {quest.tasks.some((t) => t.type === 'location') && (
        <Section icon={MapPin} label="Locations">
          {quest.tasks
            .filter((t) => t.type === 'location')
            .map((t, i) => {
              const pos = asArray(t.raw.position).map((n) => unwrapNumber(n, 0));
              const dim = unwrapString(t.raw.dimension);
              return (
                <p key={t.id ?? i} className="text-xs font-mono text-glass-300">
                  {dim ? `${dim} · ` : ''}
                  {pos.length >= 3 ? `${pos[0]} ${pos[1]} ${pos[2]}` : '—'}
                </p>
              );
            })}
        </Section>
      )}
    </div>
  );
});

function humanizeType(type: string): string {
  return type.replace(/[_-]+/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function Chip({ children, tone }: { children: React.ReactNode; tone?: 'sky' }) {
  return (
    <span
      className={
        tone === 'sky'
          ? 'px-1.5 py-0.5 rounded bg-sky-500/15 text-sky-300 border border-sky-500/25'
          : 'px-1.5 py-0.5 rounded bg-glass-100/10 text-glass-400 border border-glass-200/15'
      }
    >
      {children}
    </span>
  );
}

function Section({
  icon: Icon,
  label,
  children,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  children: React.ReactNode;
}) {
  return (
    <section>
      <h4 className="text-[11px] font-semibold uppercase tracking-wide text-glass-400 mb-1.5 inline-flex items-center gap-1.5">
        <Icon className="w-3.5 h-3.5" />
        {label}
      </h4>
      {children}
    </section>
  );
}
